import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { deleteTicket } from '@/lib/api/tickets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { cn } from '@/lib/utils.js';

type TicketLike = {
  id: string;
  ticketNumber: string;
  status: string;
  total?: string | null;
  paidTotal?: string | null;
  balanceDue?: string | null;
  customerDisplayName?: string | null;
  repairSummary?: string | null;
  deviceSummary?: string | null;
  importedFrom?: string | null;
};

export function DeleteTicketModal({
  open,
  ticket,
  customerName,
  deviceSummary,
  repairSummary,
  onClose,
  onDeleted,
}: {
  open: boolean;
  ticket: TicketLike | null;
  customerName?: string | null;
  deviceSummary?: string | null;
  repairSummary?: string | null;
  onClose: () => void;
  onDeleted: (ticketNumber: string) => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState('');
  const [confirmNumber, setConfirmNumber] = useState('');
  const [acknowledgePayments, setAcknowledgePayments] = useState(false);
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  useEffect(() => {
    if (open) {
      setStep(1);
      setReason('');
      setConfirmNumber('');
      setAcknowledgePayments(false);
    }
  }, [open, ticket?.id]);

  const deleteMut = useMutation({
    mutationFn: (body: {
      reason: string;
      confirmTicketNumber: string;
      managerOverrideAuthorizedBy: string;
      acknowledgePayments?: boolean;
    }) => deleteTicket(ticket!.id, body),
    onSuccess: (data) => {
      toastSuccess(`${data.ticketNumber} deleted`);
      onDeleted(data.ticketNumber);
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      if (parsed.code === 'HAS_ACTIVE_PAYMENTS') {
        setAcknowledgePayments(true);
        setStep(1);
        toastError(parsed.message, parsed.code);
        return;
      }
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !ticket) return null;

  const name = customerName ?? ticket.customerDisplayName ?? 'Customer';
  const device = deviceSummary ?? ticket.deviceSummary ?? null;
  const repairs = repairSummary ?? ticket.repairSummary ?? null;
  const paid =
    ticket.paidTotal != null && ticket.paidTotal !== ''
      ? Number(ticket.paidTotal)
      : Math.max(0, Number(ticket.total ?? 0) - Number(ticket.balanceDue ?? 0));
  const isImported = ticket.importedFrom === 'repairdesk';
  const showPaymentWarning = !isImported && paid > 0;
  const numberMatches =
    confirmNumber.trim().toUpperCase() === ticket.ticketNumber.trim().toUpperCase();
  const reasonOk = reason.trim().length >= 3;
  const canSubmit =
    reasonOk &&
    numberMatches &&
    (!showPaymentWarning || acknowledgePayments) &&
    !deleteMut.isPending;

  async function handleDelete() {
    if (!ticket || !canSubmit) return;
    const result = await requireOverride({
      action: 'delete_ticket',
      title: 'Manager Approval to Delete Ticket',
      description: `Permanently deleting ${ticket.ticketNumber}`,
      requiredRole: 'tenant_admin',
      context: { ticketId: ticket.id, amount: Number(ticket.total), reason: reason.trim() },
    });
    if (!result.approved) return;

    deleteMut.mutate({
      reason: reason.trim(),
      confirmTicketNumber: ticket.ticketNumber,
      managerOverrideAuthorizedBy: result.authorizedBy.id,
      ...(showPaymentWarning || acknowledgePayments ? { acknowledgePayments: true } : {}),
    });
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
        <div
          className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-label="Delete ticket"
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div className="flex items-center gap-2 text-lg font-bold text-slate-950">
              <Trash2 className="h-5 w-5 text-red-600" />
              {step === 1 ? 'Delete Ticket?' : 'Confirm Deletion'}
            </div>
            <button
              type="button"
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {step === 1 ? (
            <div className="space-y-4 p-5">
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  This action is <strong>PERMANENT</strong>. The ticket and related records will be
                  removed completely.
                </span>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                <div className="font-semibold text-slate-950">
                  {ticket.ticketNumber} — {name}
                </div>
                <div className="mt-1 text-slate-600">
                  {[device, repairs].filter(Boolean).join(' · ') || 'No device/repairs'}
                </div>
                <div className="mt-1 font-medium text-slate-800">
                  {formatCurrency(ticket.total)}
                </div>
              </div>

              <ul className="list-inside list-disc space-y-1 text-sm text-slate-600">
                <li>Repair records</li>
                <li>Notes and timeline</li>
                <li>Time entries</li>
                <li>Photos</li>
              </ul>

              {isImported ? (
                <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  This is an imported historical ticket.
                </p>
              ) : null}

              {showPaymentWarning ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  This ticket has payments totaling {formatCurrency(paid)}. Deleting does{' '}
                  <strong>NOT</strong> refund the customer.
                  <label className="mt-3 flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={acknowledgePayments}
                      onChange={(e) => setAcknowledgePayments(e.target.checked)}
                    />
                    <span>I understand — continue without refunding</span>
                  </label>
                </div>
              ) : null}

              <p className="text-sm text-slate-500">
                Use <strong>Void</strong> instead to keep the record while marking it inactive.
              </p>

              <div className="flex flex-wrap justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={showPaymentWarning && !acknowledgePayments}
                  className="rounded-xl bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50"
                  onClick={() => setStep(2)}
                >
                  I understand, continue →
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Reason for deletion *
                </label>
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  placeholder='e.g. "Test ticket", "Duplicate", "Created by mistake"'
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Type the ticket number to confirm
                </label>
                <input
                  className={cn(
                    'w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-4',
                    numberMatches
                      ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500/10'
                      : 'border-slate-300 focus:border-primary focus:ring-primary/10',
                  )}
                  placeholder={ticket.ticketNumber}
                  value={confirmNumber}
                  onChange={(e) => setConfirmNumber(e.target.value)}
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-slate-500">Type: {ticket.ticketNumber}</p>
              </div>

              <p className="text-sm text-slate-500">
                Manager approval (PIN) is required on the next step.
              </p>

              <div className="flex flex-wrap justify-between gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  onClick={() => setStep(1)}
                >
                  ← Back
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!canSubmit}
                    className="rounded-xl bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => void handleDelete()}
                  >
                    {deleteMut.isPending ? 'Deleting…' : 'Delete Permanently'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}
